import { useState } from "react";
import { toast } from "sonner";
import { DatabaseBackup, FlaskConical, Bomb, History, Download, Loader2 } from "lucide-react";
import { TrashPanel } from "@/components/core/TrashPanel";
import { WeeklyBackupPanel } from "@/components/core/WeeklyBackupPanel";
import { CreditsSettings } from "@/components/adminai/TavilyCredits";
import { Pager } from "@/components/core/Pager";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { usePaged } from "@/lib/hooks";
import { PageHeader, Panel } from "@/components/core/AppLayout";
import { DangerDialog } from "@/components/core/DangerDialog";

const BackupBox = ({ compact }) => {
  const [pwd, setPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/dati/backup", { includi_password: pwd }, { responseType: "blob" });
      const name = (r.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || "core-backup.zip";
      const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000); toast.success("Backup scaricato");
    } catch (e) { toast.error("Backup non riuscito"); }
    setBusy(false);
  };
  return (
    <div className={compact ? "rounded-xl bg-[#A6DAFC] p-4 space-y-3" : "space-y-3"} data-testid={compact ? "reset-backup-box" : "backup-box"}>
      <p className="text-sm text-slate-700">ZIP con un file JSON per ogni raccolta e un file clienti.csv leggibile con Excel. I file caricati (documenti, media) non sono inclusi: solo i metadati. Degli eventi personali e degli appunti privati sono inclusi solo i tuoi.</p>
      <label className="flex items-start gap-2 text-sm font-semibold text-[#0A1128] cursor-pointer">
        <Checkbox checked={pwd} onCheckedChange={(v) => setPwd(!!v)} className="mt-0.5" data-testid={compact ? "reset-backup-passwords" : "backup-include-passwords"} />
        <span>Includi password degli accessi in chiaro{pwd && <span className="block text-[#C8102E] font-bold">Il file conterrà password leggibili: conservalo in un luogo sicuro e non inoltrarlo.</span>}</span>
      </label>
      <Button onClick={download} disabled={busy} className="rounded-full bg-[#0A1128] hover:bg-[#E8173A] font-bold" data-testid={compact ? "reset-backup-download" : "backup-download"}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Download className="h-4 w-4 mr-1.5" />}Scarica backup
      </Button>
    </div>
  );
};

const Registry = ({ refreshKey }) => {
  const paged = usePaged("/admin/registro-eliminazioni", { k: refreshKey }, 25);
  const data = paged.items;
  return (
    <Panel className="p-6" data-testid="deletion-registry">
      <h3 className="font-display text-lg font-extrabold text-[#0A1128] flex items-center gap-2 mb-3"><History className="h-5 w-5 text-[#E8173A]" />Registro eliminazioni</h3>
      {data && !data.length && <div className="text-sm text-slate-600">Nessuna eliminazione registrata.</div>}
      <div className="divide-y divide-[#5FB3EA]/60">
        {(data || []).map((r) => (
          <div key={r.id} className="py-2.5 text-sm" data-testid={`deletion-entry-${r.id}`}>
            <div className="text-[#0A1128]"><span className="font-bold">{r.azione}</span>{r.dettaglio && ` · ${r.dettaglio}`}</div>
            <div className="text-xs text-slate-600">{fmtDateTime(r.at)} · {r.user_nome}{r.items.length > 0 && ` · ${r.items.map((i) => `${i.label}: ${i.count}`).join(", ")}`}</div>
          </div>
        ))}
      </div>
      <Pager data={paged.data} page={paged.page} setPage={paged.setPage} testid="deletion-registry-pager" />
    </Panel>
  );
};

export default function DataAdminPage() {
  const [dlg, setDlg] = useState(null);
  const [k, setK] = useState(0);
  const open = async (scope) => {
    setDlg({ scope, items: null, clienti: [] });
    try { setDlg({ scope, ...(await api.get("/admin/dati/anteprima", { params: { scope } })).data }); } catch (e) { toast.error(errMsg(e)); setDlg(null); }
  };
  const confirm = async (conferma) => {
    try {
      await api.post(dlg.scope === "demo" ? "/admin/dati/elimina-demo" : "/admin/dati/reset", { conferma });
      toast.success(dlg.scope === "demo" ? "Dati di esempio eliminati" : "Reset completato"); setDlg(null); setK((x) => x + 1);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const isReset = dlg?.scope === "reset";
  return (
    <div data-testid="data-admin-page" className="max-w-5xl">
      <PageHeader eyebrow="Amministrazione" title="Gestione dati" subtitle="Backup, eliminazione dei dati di esempio e reset completo." />
      <div className="grid grid-cols-2 gap-6 mb-6">
        <Panel className="p-6" data-testid="demo-panel">
          <h3 className="font-display text-lg font-extrabold text-[#0A1128] flex items-center gap-2 mb-1"><FlaskConical className="h-5 w-5 text-[#E8173A]" />Elimina dati di esempio</h3>
          <p className="text-sm text-slate-700 mb-4">Elimina solo i 3 clienti dimostrativi (Trattoria Da Nonna Rosa, Atelier Bianchi Moda, Studio Dentistico Dott. Ferri) e tutto ciò che è collegato. Non verranno mai ricreati. <strong>Eliminazione definitiva: non passa dal Cestino.</strong></p>
          <Button onClick={() => open("demo")} className="rounded-full bg-[#C8102E] hover:bg-[#A50D26] font-bold" data-testid="demo-delete-button">Elimina dati di esempio</Button>
        </Panel>
        <Panel className="p-6 border-2 !border-red-300" data-testid="reset-panel">
          <h3 className="font-display text-lg font-extrabold text-[#C8102E] flex items-center gap-2 mb-1"><Bomb className="h-5 w-5" />Reset completo</h3>
          <p className="text-sm text-slate-700 mb-4">Elimina tutti i clienti e tutti i dati di lavoro. Restano gli account utente, le loro impostazioni e i calendari personali (scollegati dai clienti). <strong>Eliminazione definitiva: non passa dal Cestino</strong> (svuota anche il Cestino). Scarica prima un backup.</p>
          <Button onClick={() => open("reset")} className="rounded-full bg-[#C8102E] hover:bg-[#A50D26] font-bold" data-testid="reset-button">Reset completo</Button>
        </Panel>
      </div>
      <Panel className="p-6 mb-6" data-testid="tavily-credits-panel"><h3 className="font-display text-lg font-extrabold text-[#0A1128] mb-1">Crediti ricerca web (Competitor & trend)</h3>
        <p className="text-sm text-slate-700 mb-3">Ogni ricerca web avanzata costa 2 crediti Tavily (base 1). Un'analisi usa di solito 6-10 ricerche (12-20 crediti).</p><CreditsSettings /></Panel>
      <Panel className="p-6 mb-6"><h3 className="font-display text-lg font-extrabold text-[#0A1128] flex items-center gap-2 mb-2"><DatabaseBackup className="h-5 w-5 text-[#E8173A]" />Scarica backup</h3><BackupBox /></Panel>
      <Panel className="p-6 mb-6" data-testid="real-clients-panel">
        <h3 className="font-display text-lg font-extrabold text-[#0A1128] mb-1">File di import dei clienti reali</h3>
        <p className="text-sm text-slate-700 mb-4">I 5 clienti reali (TMT, Spazio Casa, Casa Felice, Gastone Consulting, RP Home) vengono caricati in automatico una sola volta. Se servisse, puoi scaricare il file Excel pronto e importarlo da Clienti → Importa (i duplicati vengono riconosciuti e saltati).</p>
        <Button variant="outline" onClick={async () => {
          try {
            const r = await api.get("/admin/import/clienti-reali.xlsx", { responseType: "blob" });
            const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = "clienti-reali-core.xlsx"; a.click(); URL.revokeObjectURL(a.href);
          } catch (e) { toast.error(errMsg(e)); }
        }} className="rounded-full font-bold" data-testid="real-clients-download">Scarica clienti-reali-core.xlsx</Button>
      </Panel>
      <WeeklyBackupPanel onChanged={() => setK((x) => x + 1)} />
      <TrashPanel refreshKey={k} onChanged={() => setK((x) => x + 1)} />
      <Registry refreshKey={k} />
      <DangerDialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)} title={isReset ? "Reset completo dei dati?" : "Eliminare i dati di esempio?"} items={dlg?.items ?? null}
        description={dlg?.clienti?.length ? <span data-testid="danger-dialog-clients">Clienti: {dlg.clienti.join(", ")}</span> : (dlg?.items ? "Nessun cliente coinvolto." : null)}
        warning={dlg?.appuntamenti_futuri_invitati ? `Ci sono ${dlg.appuntamenti_futuri_invitati} appuntamenti futuri con inviti già inviati: verranno eliminati senza avvisare i clienti.` : null}
        onConfirm={confirm} confirmLabel={isReset ? "Esegui reset" : "Elimina dati di esempio"}>
        {isReset && <div><div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">Passo consigliato: scarica un backup prima del reset</div><BackupBox compact /></div>}
      </DangerDialog>
    </div>
  );
}
